import React, { useState } from "react";
import { Flame, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StatusChip, StatusEditor } from "@/components/attendance/AttendanceEditors";
import { summarize, AttendanceCounts, rateTone } from "@/components/MemberAttendanceCard";
import { formatDateOnly } from "@/lib/date";

// Consecutive most-recent sessions marked Present (excused sessions don't break it).
function currentStreak(sessions) {
  let n = 0;
  for (const s of sessions) {
    if (s.status === "excused") continue;
    if (s.status !== "present") break;
    n += 1;
  }
  return n;
}

function SessionLine({ s, row, canEdit, editing, setEditing, onSave }) {
  const isEditing = editing === s.event.id;
  return (
    <div className={`rounded-lg border p-2.5 transition-colors ${isEditing ? "border-slate-300 bg-slate-50" : "border-slate-200"}`} data-testid={`deepdive-session-${s.event.id}`}>
      <button type="button" disabled={!canEdit} onClick={() => setEditing(isEditing ? null : s.event.id)} className="flex w-full items-center justify-between gap-2 text-left disabled:cursor-default" data-testid={`deepdive-session-toggle-${s.event.id}`}>
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-slate-800">{s.event.title || "Session"}</span>
          <span className="block text-xs tabular-nums text-slate-500">{formatDateOnly(s.event.event_date)}</span>
          {s.note && <span className="block truncate text-xs italic text-slate-500">“{s.note}”</span>}
        </span>
        <span className="flex shrink-0 items-center gap-2"><StatusChip status={s.status} testId={`deepdive-status-${s.event.id}`} />{canEdit && <Pencil className="h-3.5 w-3.5 text-slate-300" />}</span>
      </button>
      {isEditing && (
        <div className="mt-2 border-t border-slate-200 pt-2">
          <StatusEditor status={s.status} note={s.note} testPrefix={`deepdive-edit-${s.event.id}`}
            onSave={(st, nt) => onSave(s.event.id, row.member_id, st, nt)} onDone={() => setEditing(null)} />
        </div>
      )}
    </div>
  );
}

// Coach deep dive: one member's full stats + every session (within the grid's date range).
export default function MemberDeepDive({ row, events, rangeLabel, canEdit, onSave, onClose }) {
  const [editing, setEditing] = useState(null);
  if (!row) return null;
  const sessions = [...events].reverse().filter((e) => row.cells?.[e.id])
    .map((e) => ({ event: e, status: row.cells[e.id], note: row.notes?.[e.id] }));
  const s = summarize(sessions);
  const rate = s.total ? Math.round((s.attended / s.total) * 100) : null;
  const streak = currentStreak(sessions);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="attendance-deepdive-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{row.member_name}</DialogTitle>
          <DialogDescription>Attendance · {rangeLabel}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-[auto_1fr] items-center gap-5 rounded-xl border border-slate-200 p-4">
          <div className="pr-5 sm:border-r sm:border-slate-100">
            <p className={`font-heading text-4xl font-extrabold tabular-nums ${rateTone(rate)}`} data-testid="deepdive-rate">{rate == null ? "—" : `${rate}%`}</p>
            <p className="text-xs text-slate-500">attendance rate</p>
          </div>
          <div>
            <AttendanceCounts s={s} testPrefix="deepdive" />
            <p className="mt-2 flex items-center gap-1 text-xs font-semibold text-orange-600" data-testid="deepdive-streak">
              <Flame className="h-3.5 w-3.5" /> {streak} session{streak === 1 ? "" : "s"} perfect-attendance streak
            </p>
          </div>
        </div>
        {sessions.length === 0 ? <p className="py-6 text-center text-sm text-slate-400" data-testid="deepdive-empty">No sessions recorded in this range.</p> : (
          <div className="space-y-2" data-testid="deepdive-sessions">
            {sessions.map((x) => <SessionLine key={x.event.id} s={x} row={row} canEdit={canEdit} editing={editing} setEditing={setEditing} onSave={onSave} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
